import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../design/Icon';
import useAudioPeaks from '../utils/useAudioPeaks';

interface WaveformPlayerProps {
  src: string;
  /** Text on the play button, e.g. "השמע". */
  label: string;
  /** Number of waveform bars. */
  bars?: number;
  disabled?: boolean;
  /** Called on every animation frame while playing, and on seek/stop. */
  onTime?: (seconds: number, playing: boolean) => void;
}

const formatTime = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/** Deterministic low "placeholder" bars while the real waveform loads / is unavailable. */
const placeholderPeaks = (n: number): number[] =>
  Array.from({ length: n }, (_, i) => 0.25 + 0.2 * Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.6)));

/** Audio player from the school design: play pill, mute, seekable waveform, time. */
const WaveformPlayer: React.FC<WaveformPlayerProps> = ({
  src,
  label,
  bars = 72,
  disabled = false,
  onTime,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  const decoded = useAudioPeaks(src, bars);
  const peaks = useMemo(() => decoded?.peaks ?? placeholderPeaks(bars), [decoded, bars]);
  const total = duration || decoded?.duration || 0;
  const progress = total > 0 ? Math.min(1, time / total) : 0;

  const stopLoop = (): void => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const tick = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    setTime(audio.currentTime);
    onTimeRef.current?.(audio.currentTime, !audio.paused);
    if (!audio.paused) rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    setFailed(false);
    setPlaying(false);
    setTime(0);
    setDuration(0);
    if (!src) return undefined;
    const audio = new Audio(src);
    audio.preload = 'metadata';
    audioRef.current = audio;
    const onMeta = (): void => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = (): void => {
      stopLoop();
      setPlaying(false);
      setTime(0);
      audio.currentTime = 0;
      onTimeRef.current?.(0, false);
    };
    // Fallback for when requestAnimationFrame is throttled (background tab).
    const onTimeUpdate = (): void => {
      setTime(audio.currentTime);
      onTimeRef.current?.(audio.currentTime, !audio.paused);
    };
    const onError = (): void => {
      console.error('[school] Audio failed to load', src);
      stopLoop();
      setFailed(true);
      setPlaying(false);
    };
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('error', onError);
    return () => {
      stopLoop();
      audio.pause();
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('error', onError);
      audioRef.current = null;
      onTimeRef.current?.(0, false);
    };
  }, [src]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const toggle = async (): Promise<void> => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      stopLoop();
      setPlaying(false);
      onTimeRef.current?.(audio.currentTime, false);
      return;
    }
    try {
      await audio.play();
      setPlaying(true);
      stopLoop();
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error('[school] Audio play failed', err);
      setFailed(true);
    }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>): void => {
    const audio = audioRef.current;
    if (!audio || total <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * total;
    setTime(audio.currentTime);
    onTimeRef.current?.(audio.currentTime, !audio.paused);
  };

  const isDisabled = disabled || !src || failed;

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-school-line bg-school-surface p-2.5 shadow-[0_1px_3px_rgba(20,33,61,0.06)] sm:gap-4 sm:p-3">
      <button
        type="button"
        onClick={toggle}
        disabled={isDisabled}
        className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full bg-school-primary py-1.5 pe-5 ps-1.5 font-semibold text-white shadow-[0_4px_12px_rgba(47,111,237,0.25)] transition-colors hover:bg-school-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-school-primary">
          <Icon name={playing ? 'pause' : 'play'} className="h-4 w-4" />
        </span>
        {failed ? t('school_audio_unavailable') : label}
      </button>

      <button
        type="button"
        onClick={(): void => setMuted((m) => !m)}
        disabled={isDisabled}
        aria-label={(muted ? t('school_unmute') : t('school_mute')) ?? ''}
        className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-school-neutral-soft text-school-ink transition-colors hover:bg-school-line disabled:opacity-50 sm:flex"
      >
        <Icon name={muted ? 'volumeOff' : 'volume'} className="h-5 w-5" />
      </button>

      {/* Time axis runs left-to-right like any audio waveform, also in RTL pages. */}
      <div
        dir="ltr"
        role="slider"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(time)}
        tabIndex={-1}
        onClick={seek}
        className={`relative flex h-12 min-w-0 flex-1 items-center gap-[2px] ${total > 0 ? 'cursor-pointer' : ''}`}
      >
        {peaks.map((p, i) => {
          const played = (i + 0.5) / peaks.length <= progress;
          return (
            <span
              key={i}
              className={`min-w-[1px] flex-1 rounded-full transition-colors ${
                played ? 'bg-school-primary' : decoded ? 'bg-school-line' : 'bg-school-neutral-soft'
              }`}
              style={{ height: `${Math.max(8, p * 100)}%` }}
            />
          );
        })}
        {total > 0 && (playing || time > 0) && (
          <span
            className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-school-primary"
            style={{ left: `${progress * 100}%` }}
          >
            <span className="absolute -top-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-school-primary" />
          </span>
        )}
      </div>

      <span dir="ltr" className="hidden shrink-0 text-sm tabular-nums text-school-muted sm:inline">
        {formatTime(time)} / {formatTime(total)}
      </span>
    </div>
  );
};

export default WaveformPlayer;
